import * as React from 'react'

import { inject, observer } from 'mobx-react'

import { WorkflowTypeListSelector } from '~/client/src/shared/components/WorkflowTypeListSelector'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'

interface IProps {
  selectedTypes: string[]
  onSelectionChange: (selectedTypes: string[]) => void
  onClose: () => void
  // Guarded exit to the workflow-types list (routes through the discard dialog).
  // Owned by LinkedWorkflowRestriction, which flushes the buffered selection
  // first. Undefined outside a configurator → plain-text tooltip.
  onManageInConfigurator?: () => void
  permitTypesStore?: WorkflowTypesStore
}

const LinkedWorkflowSelectionContent: React.FC<IProps> = ({
  selectedTypes,
  onSelectionChange,
  onClose,
  onManageInConfigurator,
  permitTypesStore,
}) => {
  // Local controlled state, seeded once from the type-strings. The list is a
  // controlled component, so this must update on every toggle for the checkboxes
  // to reflect immediately; the parent still receives the live type-strings and
  // commits them when the popover closes.
  const [selectedIds, setSelectedIds] = React.useState(() =>
    selectedTypes
      .map(type => permitTypesStore?.getActualTypeByType(type))
      .filter(Boolean)
      .map(type => type.id),
  )

  const handleSelectionChange = React.useCallback(
    (ids: string[]) => {
      setSelectedIds(ids)

      const types = ids
        .map(id => permitTypesStore?.getPermitTypeById(id))
        .filter(Boolean)
        .map(type => type.type)

      onSelectionChange(types)
    },
    [onSelectionChange, permitTypesStore],
  )

  return (
    // Edge-to-edge, Title inside the selector's own Root (no separate header),
    // so the selector owns its insets — mirrors the Workflow Types cell and
    // Permitted Companies. The host panel owns the 400px width
    // and height bounds; this just fills them.
    <div className="tw:flex tw:min-h-0 tw:flex-1 tw:flex-col">
      <WorkflowTypeListSelector
        useActualTypes
        title={Localization.translator.selectWorkflowTypes}
        onClose={onClose}
        selectedIds={selectedIds}
        onSelectionChange={handleSelectionChange}
        onManageInConfigurator={onManageInConfigurator}
        className="tw:flex-1 tw:min-h-0"
      />
    </div>
  )
}

export default inject('permitTypesStore')(
  observer(LinkedWorkflowSelectionContent),
)
